/**
 * Smart Queue Store - Zustand State Management
 *
 * Manages the Smart Priority Queue state including:
 * - Queue items (prioritized actions)
 * - Current item index (for swipe navigation)
 * - Filters and loading states
 *
 * Created: 2026-01-05
 */

import { create } from 'zustand';
import smartQueueService from '@/services/SmartQueueService';
import type {
  QueueItem,
  QueueStats,
  QueueFilters,
  ActionResult,
  ExecuteActionInput,
  DismissActionInput,
  SnoozeActionInput,
} from '@/types/queue';

interface SmartQueueState {
  // Data
  items: QueueItem[];
  stats: QueueStats | null;

  // UI state
  currentIndex: number;
  isLoading: boolean;
  isExecuting: boolean;
  error: string | null;

  // Filters
  filters: QueueFilters;

  // Actions
  fetchQueue: (filters?: QueueFilters) => Promise<void>;
  fetchStats: () => Promise<void>;
  executeAction: (itemId: string, input?: ExecuteActionInput) => Promise<ActionResult>;
  dismissAction: (itemId: string, input?: DismissActionInput) => Promise<ActionResult>;
  snoozeAction: (itemId: string, input?: SnoozeActionInput) => Promise<ActionResult>;
  nextItem: () => void;
  previousItem: () => void;
  setCurrentIndex: (index: number) => void;
  setFilters: (filters: QueueFilters) => void;
  addItem: (item: QueueItem) => void;
  updateItem: (item: QueueItem) => void;
  removeItem: (itemId: string) => void;
  clearError: () => void;
}

export const useSmartQueueStore = create<SmartQueueState>((set, get) => ({
  // Initial state
  items: [],
  stats: null,
  currentIndex: 0,
  isLoading: false,
  isExecuting: false,
  error: null,
  filters: {},

  /**
   * Fetch prioritized queue items
   */
  fetchQueue: async (filters?: QueueFilters) => {
    set({ isLoading: true, error: null });

    try {
      const appliedFilters = filters || get().filters;
      const items = await smartQueueService.getQueue(appliedFilters);

      set({
        items,
        filters: appliedFilters,
        currentIndex: 0,
        isLoading: false,
      });
    } catch (error) {
      // 404 means no queue items exist — show empty state, not error
      const message = error instanceof Error ? error.message : '';
      if (message.includes('Not Found') || message.includes('404')) {
        set({ items: [], currentIndex: 0, isLoading: false, error: null });
      } else {
        set({ error: message || 'Failed to fetch queue', isLoading: false });
      }
    }
  },

  /**
   * Fetch queue statistics
   */
  fetchStats: async () => {
    try {
      const stats = await smartQueueService.getStats();
      set({ stats });
    } catch (error) {
      console.error('Failed to fetch queue stats:', error);
    }
  },

  /**
   * Execute primary action (swipe right)
   */
  executeAction: async (itemId: string, input?: ExecuteActionInput) => {
    set({ isExecuting: true, error: null });

    try {
      const result = await smartQueueService.executeAction(itemId, input);

      // Remove item from queue and advance to next
      const { items, currentIndex } = get();
      const newItems = items.filter(item => item.id !== itemId);

      set({
        items: newItems,
        currentIndex: Math.min(currentIndex, newItems.length - 1),
        isExecuting: false,
      });

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to execute action';
      set({ error: message, isExecuting: false });
      throw error;
    }
  },

  /**
   * Dismiss action (swipe left)
   */
  dismissAction: async (itemId: string, input?: DismissActionInput) => {
    set({ isExecuting: true, error: null });

    try {
      const result = await smartQueueService.dismissAction(itemId, input);

      // Remove item from queue and advance to next
      const { items, currentIndex } = get();
      const newItems = items.filter(item => item.id !== itemId);

      set({
        items: newItems,
        currentIndex: Math.min(currentIndex, newItems.length - 1),
        isExecuting: false,
      });

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to dismiss action';
      set({ error: message, isExecuting: false });
      throw error;
    }
  },

  /**
   * Snooze action
   */
  snoozeAction: async (itemId: string, input?: SnoozeActionInput) => {
    set({ isExecuting: true, error: null });

    try {
      const result = await smartQueueService.snoozeAction(itemId, input);

      // Remove item from queue (will reappear after snooze period)
      const { items, currentIndex } = get();
      const newItems = items.filter(item => item.id !== itemId);

      set({
        items: newItems,
        currentIndex: Math.min(currentIndex, newItems.length - 1),
        isExecuting: false,
      });

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to snooze action';
      set({ error: message, isExecuting: false });
      throw error;
    }
  },

  /**
   * Navigate to next item
   */
  nextItem: () => {
    const { items, currentIndex } = get();
    if (currentIndex < items.length - 1) {
      set({ currentIndex: currentIndex + 1 });
    }
  },

  /**
   * Navigate to previous item
   */
  previousItem: () => {
    const { currentIndex } = get();
    if (currentIndex > 0) {
      set({ currentIndex: currentIndex - 1 });
    }
  },

  /**
   * Set current index directly
   */
  setCurrentIndex: (index: number) => {
    const { items } = get();
    if (index >= 0 && index < items.length) {
      set({ currentIndex: index });
    }
  },

  /**
   * Update filters
   */
  setFilters: (filters: QueueFilters) => {
    set({ filters });
  },

  /**
   * Add new item (from WebSocket)
   */
  addItem: (item: QueueItem) => {
    const { items } = get();

    // Insert in priority order
    const newItems = [...items, item].sort((a, b) => b.priority - a.priority);

    set({ items: newItems });
  },

  /**
   * Update existing item (from WebSocket)
   */
  updateItem: (updatedItem: QueueItem) => {
    const { items } = get();

    const newItems = items
      .map(item => (item.id === updatedItem.id ? updatedItem : item))
      .sort((a, b) => b.priority - a.priority);

    set({ items: newItems });
  },

  /**
   * Remove item (from WebSocket or action)
   */
  removeItem: (itemId: string) => {
    const { items, currentIndex } = get();
    const newItems = items.filter(item => item.id !== itemId);

    set({
      items: newItems,
      currentIndex: Math.min(currentIndex, Math.max(0, newItems.length - 1)),
    });
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },
}));

// Selectors
export const selectCurrentItem = (state: SmartQueueState) =>
  state.items[state.currentIndex] || null;

export const selectRemainingItems = (state: SmartQueueState) =>
  state.items.length - state.currentIndex;

export const selectHasItems = (state: SmartQueueState) =>
  state.items.length > 0;

export const selectIsAtStart = (state: SmartQueueState) =>
  state.currentIndex === 0;

export const selectIsAtEnd = (state: SmartQueueState) =>
  state.currentIndex >= state.items.length - 1;
